import { Link } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { usePageMeta } from "@/hooks/usePageMeta";

const team = [
  { icon: "ri-edit-2-line", name: "内容编辑组", desc: "负责选题策划与文章编写，坚持循证、客观、易读的表达。" },
  { icon: "ri-stethoscope-line", name: "医学顾问", desc: "对健康相关内容进行专业审阅，确保科普表述准确、不过度承诺。" },
  { icon: "ri-user-heart-line", name: "用户支持", desc: "倾听读者反馈，持续改进内容与阅读体验。" },
];

const values = [
  { icon: "ri-flask-line", title: "循证为本", desc: "内容优先参考权威医学机构与同行评审研究，拒绝无依据的说法。" },
  { icon: "ri-heart-2-line", title: "温和友善", desc: "不评判、不制造焦虑，用理解与尊重的态度谈论每一个话题。" },
  { icon: "ri-lock-line", title: "尊重隐私", desc: "浏览与收藏均在你的设备本地完成，我们不会收集敏感个人信息。" },
  { icon: "ri-refresh-line", title: "持续更新", desc: "随着研究进展不断修订内容，让信息保持可靠与实用。" },
];

export default function About() {
  usePageMeta(
    "关于我们 - 自慰健康生活资讯",
    "了解自慰健康生活资讯站的定位与服务理念、运营团队、版权与隐私声明，我们用科学与温和的方式陪伴你的自我关怀。",
    "关于我们,自慰,健康生活,隐私政策,版权声明",
  );

  return (
    <div className="w-full">
      <section className="relative w-full h-[280px] md:h-[380px] overflow-hidden bg-primary-950">
        <img
          src="https://readdy.ai/api/search-image?query=calm%20abstract%20wellness%20background%20with%20soft%20teal%20waves%20and%20warm%20sand%20light%2C%20minimal%20editorial%20style&width=1600&height=900&seq=about-hero&orientation=landscape"
          alt="关于自慰健康生活资讯站"
          title="关于我们 自慰健康生活资讯"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-primary-950/90 via-primary-950/50 to-primary-950/30"></div>
        <div className="relative z-10 w-full h-full flex items-end">
          <div className="w-full max-w-[1200px] mx-auto px-4 md:px-8 pb-8 md:pb-12">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-background-50">关于我们</h1>
            <p className="mt-3 text-sm md:text-base text-background-100/90 max-w-2xl">
              我们希望成为你了解身体、照顾自己时，一个值得信赖的信息来源。
            </p>
          </div>
        </div>
      </section>

      <div className="w-full max-w-[1200px] mx-auto px-4 md:px-8 py-8 md:py-12">
        <Breadcrumb items={[{ label: "关于我们" }]} />

        {/* 定位与理念 */}
        <section className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start">
          <div>
            <span className="inline-flex items-center gap-2 text-sm text-primary-600 mb-3">
              <i className="ri-flag-2-line"></i>
              网站定位
            </span>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 leading-snug">
              面向成年人的健康生活资讯平台
            </h2>
            <div className="mt-5 space-y-4 text-sm md:text-base text-foreground-700 leading-relaxed">
              <p>
                自慰网站围绕自慰的科学知识、自我关怀、心理健康与性健康教育展开，面向 18 岁以上的普通读者。
              </p>
              <p>
                我们相信，了解身体不是一件需要羞耻的事。当信息足够清晰准确，人就能少一些焦虑，多一些从容。
              </p>
              <p>
                因此，我们坚持用科学的态度组织内容，用平和的语气与读者交流，让健康知识真正可读、可用。
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {values.map((value) => (
              <div key={value.title} className="rounded-lg bg-background-100 border border-background-200 p-5">
                <span className="w-11 h-11 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 text-xl">
                  <i className={value.icon}></i>
                </span>
                <h3 className="mt-4 text-base font-medium text-foreground-900">{value.title}</h3>
                <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{value.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* 团队 */}
        <section className="mt-12 md:mt-16">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">运营团队</h2>
          <p className="mt-2 text-sm text-foreground-600">一个由内容编辑、医学顾问与用户支持组成的小团队。</p>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
            {team.map((member) => (
              <div key={member.name} className="rounded-lg bg-background-50 border border-background-200 p-6">
                <span className="w-12 h-12 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-700 text-2xl">
                  <i className={member.icon}></i>
                </span>
                <h3 className="mt-4 text-lg font-medium text-foreground-900">{member.name}</h3>
                <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{member.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* 版权与隐私 */}
        <section className="mt-12 md:mt-16 rounded-lg bg-background-100 border border-background-200 p-6 md:p-10">
          <h2 className="font-heading text-2xl font-bold text-foreground-950">版权与隐私声明</h2>
          <div className="mt-5 space-y-4 text-sm text-foreground-700 leading-relaxed">
            <p>
              <strong className="text-foreground-900">内容说明：</strong>
              本站所有内容均为健康科普资讯，仅供参考与学习使用，不能替代专业医疗诊断或治疗建议。如需诊疗，请及时就医。
            </p>
            <p>
              <strong className="text-foreground-900">版权声明：</strong>
              本站文字与版面内容版权归本站所有，未经授权请勿用于商业用途。转载请注明来源。
            </p>
            <p>
              <strong className="text-foreground-900">隐私说明：</strong>
              本站的收藏与阅读记录保存在你的浏览器本地，不会上传服务器。联系反馈表单收集的信息仅用于回复你的问题。
            </p>
            <p>
              <strong className="text-foreground-900">适用人群：</strong>本站内容面向 18 岁以上成年人。
            </p>
          </div>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              返回首页
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-background-50 text-foreground-800 text-sm border border-background-200 hover:bg-background-200 transition-colors whitespace-nowrap"
            >
              联系反馈
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
}